import React from 'react'
import { twMerge } from 'tailwind-merge'

import { HStack } from '../hstack'
import { Pressable } from '../pressable'
import { Text } from '../text'

export type ChipToggleOption = {
    value: string
    label: string
}

export type ChipToggleGroupProps = {
    options: ChipToggleOption[]
    value: string[]
    onChange: (value: string[]) => void
    disabled?: boolean
    fill?: boolean
    className?: string
    /** Per-chip overrides — e.g. to fit a full week of chips into a narrow column. */
    chipClassName?: string
    testID?: string
}

export const ChipToggleGroup: React.FC<ChipToggleGroupProps> = ({
    options,
    value,
    onChange,
    disabled = false,
    fill = false,
    className,
    chipClassName,
    testID,
}) => (
    <HStack
        className={twMerge(fill ? 'w-full gap-4' : 'flex-wrap gap-2', className)}
        testID={testID}
    >
        {options.map(option => {
            const isSelected = value.includes(option.value)
            return (
                <Pressable
                    key={option.value}
                    onPress={() =>
                        onChange(
                            isSelected
                                ? value.filter(v => v !== option.value)
                                : [...value, option.value],
                        )
                    }
                    accessibilityRole='button'
                    accessibilityState={{ selected: isSelected, disabled }}
                    className={twMerge(
                        'h-10 items-center justify-center rounded border-solid bg-surface-highlight px-3',
                        fill ? 'min-w-px flex-1' : 'min-w-[52px]',
                        isSelected
                            ? 'border-2 border-action-default'
                            : fill
                              ? 'border-2 border-border-subtle'
                              : 'border-2 border-transparent',
                        disabled
                            ? 'pointer-events-none opacity-40'
                            : 'hover:bg-surface-highlight-hover',
                        chipClassName,
                    )}
                >
                    <Text size='body' weight={fill ? 'semiBold' : 'regular'} tone='default'>
                        {option.label}
                    </Text>
                </Pressable>
            )
        })}
    </HStack>
)

ChipToggleGroup.displayName = 'ChipToggleGroup'
